// Shared loader for the Catalyst Web SDK.
// Used by StaffLogin (sign-in widget) and AuthContext (SSO sign-out).
// Idempotent — multiple callers awaiting load() share a single fetch.

const SDK_SCRIPT = 'https://static.zohocdn.com/catalyst/sdk/js/4.5.0/catalystWebSDK.js';
const INIT_SCRIPT = '/__catalyst/sdk/init.js';

let loadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const already = Array.from(document.scripts).some((s) => s.src && s.src.endsWith(src));
    if (already) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export function loadCatalystSdk() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    await loadScript(SDK_SCRIPT);
    await loadScript(INIT_SCRIPT);
    if (!window.catalyst || !window.catalyst.auth) {
      loadPromise = null; // allow retry
      throw new Error('Catalyst Web SDK did not initialise');
    }
    return window.catalyst;
  })();
  return loadPromise;
}

// Terminate the Catalyst SSO session. Per Catalyst docs:
// https://docs.catalyst.zoho.com/en/sdk/web/v4/cloud-scale/authentication/sign-out-user/
// Pass an absolute redirect URL the user lands on after sign-out completes.
// Returns a promise that resolves when the SDK call has been issued — the
// SDK itself navigates the page, so most callers don't need to await this.
export async function catalystSignOut(redirectUrl) {
  try {
    const catalyst = await loadCatalystSdk();
    if (catalyst && catalyst.auth && typeof catalyst.auth.signOut === 'function') {
      catalyst.auth.signOut(redirectUrl);
      return true;
    }
  } catch (err) {
    // SDK failed to load (offline, ad-blocker, etc.) — caller still has the
    // localStorage signed-out flag fallback. Log and move on.
    console.warn('[catalystSdk] signOut unavailable:', err && err.message);
  }
  return false;
}
